import React from 'react'
import { Region } from '@moonpig/web-core-types'
import {
  CustomLink,
  PrimaryButton,
  TertiaryButton,
} from '@moonpig/launchpad-components'
import { EventObject, TrackedAnchor } from '@moonpig/web-core-analytics'

import { createUtilitiesGaEvent } from './analytics'
import { UtilityName } from './types'
import { useIsMobile } from '../../hooks/useIsMobile'

const makeUtilityTrackingAnchor = (
  eventPayload: EventObject,
  testId: string,
) => {
  const UtilityTrackingAnchor = (
    props: React.ComponentProps<typeof CustomLink>,
  ) => {
    return (
      <TrackedAnchor data-testid={testId} {...props} eventData={eventPayload} />
    )
  }

  return UtilityTrackingAnchor
}

const makeTrackedUtilityButton = (
  eventPayload: EventObject,
  testId: string,
  label: string,
  isConciergeSearchExperimentEnabled: boolean,
) => {
  const UtilityTrackingButton = (
    props: React.ComponentProps<typeof CustomLink>,
  ) => {
    const isMobile = useIsMobile()

    const ButtonComponent =
      isMobile || isConciergeSearchExperimentEnabled
        ? TertiaryButton
        : PrimaryButton

    return (
      <TrackedAnchor
        style={{ height: '45px', ...(isMobile ? { marginLeft: 10 } : {}) }}
        data-testid={testId}
        {...props}
        eventData={eventPayload}
      >
        <ButtonComponent
          style={{
            height: '45px',
            width: '100px',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
          }}
        >
          {label}
        </ButtonComponent>
      </TrackedAnchor>
    )
  }

  return UtilityTrackingButton
}

export const createUtilityItems = ({
  region,
  loggedIn,
  returnUrl,
  localisedLabels,
  basketTotalItems,
  isHeaderSignInExperimentEnabled,
  isConciergeSearchExperimentEnabled,
}: {
  region: Region
  loggedIn: boolean
  returnUrl: string
  localisedLabels: { [key: string]: string }
  basketTotalItems: number
  isHeaderSignInExperimentEnabled: boolean
  isConciergeSearchExperimentEnabled: boolean
}) => {
  const basketUtilityItem = {
    to: `/${region}/basket/`,
    type: 'basket' as const,
    label: localisedLabels.basket,
    linkAs: makeUtilityTrackingAnchor(
      createUtilitiesGaEvent(UtilityName.BASKET),
      'lp-nav-utilitiles-basket',
    ),
    count: basketTotalItems,
  }

  const myAccountUtilityItem = {
    to: `/${region}/myaccount/`,
    type: 'account' as const,
    label: localisedLabels.account,
    linkAs: makeUtilityTrackingAnchor(
      createUtilitiesGaEvent(UtilityName.MY_ACCOUNT),
      'lp-nav-utilitiles-my-account',
    ),
    tooltip: localisedLabels.accountTooltip,
  }

  const loginUtilityItem = {
    to: `/${region}/account/login/?returnUrl=${returnUrl}`,
    type: 'account' as const,
    label: localisedLabels.login,
    linkAs: makeTrackedUtilityButton(
      createUtilitiesGaEvent(UtilityName.ACCOUNT_LOGIN),
      'lp-nav-utilitiles-login',
      localisedLabels.login,
      isConciergeSearchExperimentEnabled,
    ),
  }

  if (isHeaderSignInExperimentEnabled && !loggedIn) {
    return [basketUtilityItem, loginUtilityItem]
  }

  return [myAccountUtilityItem, basketUtilityItem]
}
